<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.plans.detail_title') }}
          </h1>
          <span class="h-20px border-gray-300 border-start mx-4"></span>
          <span class="text-muted fs-6">{{ plan?.name }}</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <router-link to="/landlord/plans" class="btn btn-sm btn-light">
            <i class="fas fa-arrow-left me-1"></i> {{ $t('landlord.plans.back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">

        <div v-if="loading" class="text-center py-20">
          <span class="spinner-border text-primary"></span>
        </div>

        <template v-else-if="plan">
          <!-- Plan info -->
          <div class="card card-flush mb-8">
            <div class="card-body d-flex align-items-center justify-content-between">
              <div>
                <h2 class="fw-bolder mb-1">{{ plan.name }}</h2>
                <span class="fs-4 fw-bold text-primary">{{ formatPrice(plan.price) }}</span>
                <span v-if="plan.billing_period" class="text-muted fs-7"> / {{ $t('landlord.plans.period_' + plan.billing_period) }}</span>
                <span class="badge ms-3" :class="plan.is_active ? 'badge-light-success' : 'badge-light-danger'">
                  {{ plan.is_active ? $t('landlord.plans.active') : $t('landlord.plans.inactive') }}
                </span>
                <p v-if="plan.description" class="text-gray-500 mt-2 mb-0">{{ plan.description }}</p>
              </div>
            </div>
          </div>

          <!-- Modules sync card -->
          <div class="card card-flush">
            <div class="card-header border-0 pt-6">
              <div class="card-title">
                <h4 class="fw-bolder">{{ $t('landlord.plans.sync_title') }}</h4>
              </div>
              <div class="card-toolbar">
                <button @click="onSyncModules" class="btn btn-sm btn-primary" :disabled="syncing">
                  <span v-if="syncing" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="fas fa-save me-1"></i>
                  {{ $t('landlord.plans.sync_save') }}
                </button>
              </div>
            </div>
            <div class="card-body py-4">
              <div v-if="loadingModules" class="text-center py-10">
                <span class="spinner-border text-primary"></span>
              </div>
              <div v-else>
                <p class="text-gray-500 fs-7 mb-5">{{ $t('landlord.plans.sync_description') }}</p>
                <!-- Module tree with checkboxes -->
                <div v-for="mod in allModules" :key="mod.id" class="mb-5">
                  <div class="d-flex align-items-center bg-light rounded p-3 mb-2">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox"
                             :id="'mod-' + mod.id"
                             :checked="selectedModuleIds.includes(mod.id)"
                             @change="toggleParentModule(mod)" />
                      <label class="form-check-label fw-bold" :for="'mod-' + mod.id">
                        <i :class="mod.icon || 'fas fa-puzzle-piece'" class="text-primary me-2"></i>
                        {{ mod.name }}
                        <span class="text-gray-500 fs-8 ms-1">({{ mod.slug }})</span>
                      </label>
                    </div>
                  </div>
                  <!-- Children -->
                  <div v-if="mod.children && mod.children.length" class="ms-10">
                    <div v-for="child in mod.children" :key="child.id" class="d-flex align-items-center mb-2">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox"
                               :id="'mod-' + child.id"
                               :checked="selectedModuleIds.includes(child.id)"
                               @change="toggleChildModule(child, mod)" />
                        <label class="form-check-label" :for="'mod-' + child.id">
                          {{ child.name }}
                          <span class="text-gray-500 fs-8 ms-1">({{ child.slug }})</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
    name: 'LandlordPlanView',
    data() {
        return {
            plan: null,
            allModules: [],
            selectedModuleIds: [],
            loading: true,
            loadingModules: false,
            syncing: false,
        }
    },
    computed: {
        planId() {
            return this.$route.params.id
        },
    },
    mounted() {
        this.loadData()
    },
    methods: {
        ...mapActions('landlord', ['fetchPlan', 'fetchModules', 'syncPlanModules']),

        formatPrice(price) {
            return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
        },

        async loadData() {
            this.loading = true
            try {
                const [planRes, modulesRes] = await Promise.all([
                    this.fetchPlan(this.planId),
                    this.fetchModules(),
                ])
                this.plan = planRes.data.data
                this.allModules = modulesRes.data.data
                this.selectedModuleIds = this.plan.modules ? this.plan.modules.map(m => m.id) : []
            } finally {
                this.loading = false
            }
        },

        toggleParentModule(mod) {
            const idx = this.selectedModuleIds.indexOf(mod.id)
            if (idx > -1) {
                // Uncheck parent and all children
                this.selectedModuleIds = this.selectedModuleIds.filter(id => id !== mod.id)
                if (mod.children) {
                    mod.children.forEach(child => {
                        this.selectedModuleIds = this.selectedModuleIds.filter(id => id !== child.id)
                    })
                }
            } else {
                // Check parent and all children
                this.selectedModuleIds.push(mod.id)
                if (mod.children) {
                    mod.children.forEach(child => {
                        if (!this.selectedModuleIds.includes(child.id)) {
                            this.selectedModuleIds.push(child.id)
                        }
                    })
                }
            }
        },

        toggleChildModule(child, parent) {
            const idx = this.selectedModuleIds.indexOf(child.id)
            if (idx > -1) {
                this.selectedModuleIds = this.selectedModuleIds.filter(id => id !== child.id)
            } else {
                this.selectedModuleIds.push(child.id)
                // Auto-check parent if not already
                if (!this.selectedModuleIds.includes(parent.id)) {
                    this.selectedModuleIds.push(parent.id)
                }
            }
        },

        onSyncModules() {
            this.syncing = true
            this.syncPlanModules({ planId: this.planId, moduleIds: this.selectedModuleIds }).then((res) => {
                this.plan = res.data.data
                this.selectedModuleIds = this.plan.modules ? this.plan.modules.map(m => m.id) : []
                window.toastr['success'](i18n.global.t('landlord.plans.sync_success'))
            }).finally(() => {
                this.syncing = false
            })
        },
    }
}
</script>
